/**
 * @fileoverview The filter toolbar above the Sales Pipeline backlog table.
 * @description Search plus the table's selects: state, bank type, tier, lead rating, branch band,
 * backlog-only and archive status (Active always; Pending only for a viewer who may see pending
 * submissions; Archived and All only for one who may archive or restore). The clear button stays
 * mounted so toggling filters never flips the row between one and two lines.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import Select from '@/shared/components/atoms/Select'

import TableSearchInput from './TableSearchInput'

import type { BacklogFilters } from '@/features/sales/lib/pipeline-filters.utils'
import type {
  ProspectArchiveStatus,
  ProspectBranchBand,
  ProspectLeadRating,
  ProspectMetadata,
} from '@/features/sales/types/prospect.types'

const BRANCH_BANDS: ProspectBranchBand[] = ['UP_TO_10', 'FROM_11_TO_20', 'FROM_21']

interface PipelineFiltersBarProps {
  filters: BacklogFilters
  metadata: ProspectMetadata
  onChange: (change: Partial<BacklogFilters>) => void
  onClear: () => void
  anyFilter: boolean
  canSeePending: boolean
  canSeeArchived: boolean
}

/**
 * @component
 * @param {PipelineFiltersBarProps} props - The filters, their choices and the handlers.
 * @param {BacklogFilters} props.filters - The current filters.
 * @param {ProspectMetadata} props.metadata - The catalogue choices for the selects.
 * @param {(change: Partial<BacklogFilters>) => void} props.onChange - Applies a filter change.
 * @param {() => void} props.onClear - Resets every filter.
 * @param {boolean} props.anyFilter - Whether any filter is set (enables the clear button).
 * @param {boolean} props.canSeePending - Whether the Pending status is offered.
 * @param {boolean} props.canSeeArchived - Whether the Archived and All statuses are offered.
 */
function PipelineFiltersBar({
  filters,
  metadata,
  onChange,
  onClear,
  anyFilter,
  canSeePending,
  canSeeArchived,
}: PipelineFiltersBarProps) {
  const { t } = useTranslation()
  const statusOptions: { value: ProspectArchiveStatus; label: string }[] = [
    { value: 'ACTIVE', label: t('sales.pipeline.filters.active') },
    ...(canSeePending
      ? [{ value: 'PENDING' as const, label: t('sales.pipeline.filters.pendingStatus') }]
      : []),
    ...(canSeeArchived
      ? [
          { value: 'ARCHIVED' as const, label: t('sales.pipeline.filters.archived') },
          { value: 'ALL' as const, label: t('sales.pipeline.filters.all') },
        ]
      : []),
  ]

  return (
    <div className="flex flex-wrap items-center gap-2.5 p-3.5 sm:p-4">
      <TableSearchInput
        value={filters.query}
        onChange={(query) => onChange({ query })}
        placeholder={t('sales.pipeline.searchPlaceholder')}
        data-testid="sales-pipeline-search-input"
      />
      <Select
        aria-label={t('sales.pipeline.columns.state')}
        data-testid="sales-pipeline-state-select"
        value={filters.stateId}
        onChange={(event) => onChange({ stateId: event.target.value })}
        options={[
          { value: '', label: t('sales.pipeline.filters.allIndia') },
          ...metadata.states.map((state) => ({ value: state.id, label: state.name })),
        ]}
      />
      <Select
        aria-label={t('sales.pipeline.columns.bankType')}
        data-testid="sales-pipeline-bank-type-select"
        value={filters.bankTypeId}
        onChange={(event) => onChange({ bankTypeId: event.target.value })}
        options={[
          { value: '', label: t('sales.pipeline.filters.allTypes') },
          ...metadata.bankTypes.map((type) => ({ value: type.id, label: type.name })),
        ]}
      />
      <Select
        aria-label={t('sales.pipeline.columns.tier')}
        data-testid="sales-pipeline-tier-select"
        value={filters.tierCode}
        onChange={(event) => onChange({ tierCode: event.target.value })}
        options={[
          { value: '', label: t('sales.pipeline.filters.allTiers') },
          ...metadata.tierScheme.ranges.map((range) => ({ value: range.code, label: range.name })),
        ]}
      />
      <Select
        aria-label={t('sales.pipeline.columns.rating')}
        data-testid="sales-pipeline-lead-rating-select"
        value={filters.leadRating}
        onChange={(event) =>
          onChange({ leadRating: event.target.value as ProspectLeadRating | '' })
        }
        options={[
          { value: '', label: t('sales.pipeline.filters.allRatings') },
          ...metadata.leadRatings.map((rating) => ({
            value: rating,
            label: t(`sales.pipeline.ratings.${rating}`),
          })),
        ]}
      />
      <Select
        aria-label={t('sales.pipeline.columns.branches')}
        data-testid="sales-pipeline-branch-band-select"
        value={filters.branchBand}
        onChange={(event) =>
          onChange({ branchBand: event.target.value as ProspectBranchBand | '' })
        }
        options={[
          { value: '', label: t('sales.pipeline.filters.allBranchSizes') },
          ...BRANCH_BANDS.map((band) => ({
            value: band,
            label: t(`sales.pipeline.filters.branchBands.${band}`),
          })),
        ]}
      />
      <Select
        aria-label={t('sales.pipeline.filters.targetScope')}
        data-testid="sales-pipeline-backlog-select"
        value={filters.backlogOnly ? 'BACKLOG' : 'ALL'}
        onChange={(event) => onChange({ backlogOnly: event.target.value === 'BACKLOG' })}
        options={[
          { value: 'ALL', label: t('sales.pipeline.filters.allProspects') },
          { value: 'BACKLOG', label: t('sales.pipeline.filters.backlogOnly') },
        ]}
      />
      <Select
        aria-label={t('sales.pipeline.filters.active')}
        data-testid="sales-pipeline-archive-status-select"
        value={filters.archiveStatus}
        onChange={(event) =>
          onChange({ archiveStatus: event.target.value as ProspectArchiveStatus })
        }
        options={statusOptions}
      />
      <button
        type="button"
        onClick={onClear}
        disabled={!anyFilter}
        aria-hidden={!anyFilter}
        data-testid="sales-pipeline-clear-filters-button"
        className={`rounded-lg border border-input-border bg-white px-3.5 py-2 text-[13px] font-semibold text-slate ${
          anyFilter ? 'cursor-pointer hover:border-border-hover hover:text-ink' : 'invisible'
        }`}
      >
        {t('sales.pipeline.filters.clear')}
      </button>
    </div>
  )
}

export default PipelineFiltersBar
